import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";
import Header from "../Header/header";
import Fotter from "../Fotter/fotter";
import API_URL from "../../config";

import {
  Search,
  MapPin,
  Hotel,
  Star,
  ArrowRight,
  Filter,
  Building2,
  CheckCircle2,
  Zap,
  ShieldCheck,
  Coins,
  Loader2,
  Waves,
  Mountain,
  Palmtree,
  BedDouble
} from "lucide-react";

function UserAccommodation() {
  const [accommodations, setAccommodations] = useState([]);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("All");
  const [filterLocation, setFilterLocation] = useState("All");
  const [priceRange, setPriceRange] = useState([0, 500000]);
  const [isLoading, setIsLoading] = useState(true);

  const { user } = useAuth();
  const navigate = useNavigate();

  const sriLankaCities = [
    "All", "Colombo", "Kandy", "Galle", "Jaffna", "Negombo", "Trincomalee",
    "Anuradhapura", "Nuwara Eliya", "Matara", "Ella", "Hikkaduwa", "Unawatuna"
  ];

  useEffect(() => {
    const fetchAccommodations = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_URL}/accommodation`);
        const data = await response.json();
        if (data.accommodations) {
          setAccommodations(data.accommodations);
        }
      } catch (err) {
        console.error("Error fetching accommodations:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAccommodations();
  }, []);

  const filteredAccommodations = accommodations.filter((acc) => {
    const matchesSearch =
      acc.accommodationName.toLowerCase().includes(search.toLowerCase()) ||
      acc.description.toLowerCase().includes(search.toLowerCase());

    const matchesType = filterType === "All" || acc.type.toLowerCase() === filterType.toLowerCase();
    const matchesLocation = filterLocation === "All" || acc.location.toLowerCase() === filterLocation.toLowerCase();
    const matchesPrice = acc.pricePerNight >= priceRange[0] && acc.pricePerNight <= priceRange[1];

    return matchesSearch && matchesType && matchesLocation && matchesPrice;
  });

  const handleBookNow = (acc) => {
    if (!acc.availability) return;
    if (!user) {
      toast.info("Secure elite member rates by logging in first.");
      navigate("/login");
      return;
    }
    navigate("/booking-form", { state: acc });
  };

  return (
    <div className="bg-white text-sky-950 font-sans selection:bg-sky-100 selection:text-sky-900">
      <Header />

      {/* Luxury Property Hero */}
      <section className="relative min-h-[65vh] flex items-center justify-center flex-col">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?q=80&w=2000&auto=format&fit=crop"
            className="w-full h-full object-cover scale-105 animate-slow-zoom"
            alt="Property Background"
            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=1200&q=80"; }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-sky-950/50 via-sky-950/30 to-transparent" />
        </div>

        <div className="relative z-30 max-w-6xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 text-white text-[10px] font-black uppercase tracking-[0.3em] mb-8 animate-fade-in shadow-2xl">
            <BedDouble size={12} className="text-sky-300" />
            Curated Sanctuaries 2026
          </div>
          <h1 className="text-6xl md:text-8xl font-black text-white mb-8 drop-shadow-2xl animate-slide-up leading-none tracking-tighter">
            Elite Hideaways
          </h1>
          <p className="text-lg md:text-2xl text-white/80 font-medium mb-16 max-w-3xl mx-auto drop-shadow-md animate-slide-up-delayed leading-relaxed">
            From colonial villas in the misty hills to ultra-modern beachfront escapes, discover your perfect sanctuary.
          </p>

          {/* Quick Search Hub */}
          <div className="max-w-4xl mx-auto bg-white/95 backdrop-blur-xl p-3 rounded-[2.5rem] shadow-2xl flex flex-col lg:flex-row items-center gap-3 border border-white/20 relative z-30">
            <div className="flex-grow flex items-center gap-4 pl-8 w-full py-4 lg:py-0">
              <Search size={22} className="text-sky-400" />
              <input
                type="text"
                placeholder="Search by hotel name, city, or vibe..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-transparent outline-none font-bold placeholder:text-gray-300 text-sky-950 text-lg"
              />
            </div>
            <div className="h-12 w-px bg-gray-100 hidden lg:block" />
            <div className="flex items-center gap-4 px-6 py-4 lg:py-0 w-full lg:w-auto">
              <MapPin size={20} className="text-rose-400" />
              <select
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
                className="bg-transparent font-black text-[10px] uppercase tracking-widest text-sky-900 outline-none cursor-pointer"
              >
                {sriLankaCities.map(city => <option key={city} value={city}>{city}</option>)}
              </select>
            </div>
            <button className="w-full lg:w-auto px-12 py-5 bg-sky-950 text-white font-black rounded-[2rem] hover:bg-sky-800 transition-all flex items-center justify-center gap-3 shadow-xl active:scale-95 group">
              <Building2 size={20} />
              DISCOVER
            </button>
          </div>
        </div>
      </section>

      {/* Property Inventory Hub */}
      <section className="max-w-7xl mx-auto px-6 relative z-20 pb-32">
        <div className="bg-white rounded-[4rem] shadow-2xl border border-gray-50 p-10 md:p-16 overflow-hidden">

          <div className="flex flex-col lg:flex-row gap-16">
            {/* Left Column: Filter Sidebar */}
            <aside className="lg:w-80 space-y-12">
              <div className="sticky top-10">
                <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-sky-900/40 mb-10 flex items-center gap-3">
                  <Filter size={16} className="text-sky-500" />
                  Inventory Filters
                </h3>

                <div className="space-y-10">
                  {/* Property Vibe */}
                  <div className="space-y-5">
                    <label className="text-[11px] font-black tracking-widest uppercase text-sky-950">Property Focus</label>
                    <div className="grid grid-cols-2 gap-3">
                      {["All", "Hotel", "Villa", "Resort", "Apartment"].map(type => (
                        <button
                          key={type}
                          onClick={() => setFilterType(type)}
                          className={`
                            px-4 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all
                            ${filterType === type
                              ? "bg-sky-950 text-white shadow-2xl shadow-sky-950/20 ring-4 ring-sky-500/10"
                              : "bg-gray-50 text-gray-400 hover:bg-white hover:shadow-lg border border-transparent hover:border-gray-100"}
                          `}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Budget Scale */}
                  <div className="space-y-5">
                    <div className="flex items-center justify-between">
                      <label className="text-[11px] font-black tracking-widest uppercase text-sky-950">Nightly Rate</label>
                      <span className="text-[9px] font-black text-sky-800 bg-sky-50 px-3 py-1 rounded-full">
                        {priceRange[1].toLocaleString()} LKR
                      </span>
                    </div>
                    <div className="relative pt-2">
                      <input
                        type="range"
                        min="0"
                        max="500000"
                        step="5000"
                        value={priceRange[1]}
                        onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
                        className="w-full h-1.5 bg-gray-100 rounded-full appearance-none cursor-pointer accent-sky-800 hover:accent-sky-700 transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Exclusive Benefit Card */}
                <div className="mt-12 bg-sky-950 rounded-[2.5rem] p-10 text-white relative overflow-hidden group/benefit shadow-2xl shadow-sky-950/20">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-sky-500/10 rounded-full blur-3xl -mr-20 -mt-20 group-hover/benefit:scale-150 transition-transform duration-1000" />
                  <Zap className="text-sky-400 mb-6" size={32} />
                  <h4 className="text-2xl font-black mb-3 italic tracking-tight underline-offset-4 decoration-sky-500/20 underline">Elite Loyalty</h4>
                  <p className="text-[10px] font-bold text-white/50 leading-relaxed mb-8 uppercase tracking-widest uppercase">
                    Members enjoy complimentary <span className="text-sky-300">Sanctuary Upgrades</span> and private beach access.
                  </p>
                  <button className="w-full py-4 bg-white text-sky-950 text-[9px] font-black uppercase tracking-widest rounded-xl hover:translate-y-[-2px] transition-all shadow-xl active:scale-95">
                    Claim Privilege
                  </button>
                </div>

                {/* Quick Vibe Icons */}
                <div className="grid grid-cols-3 gap-3 mt-10">
                  <div className="flex flex-col items-center gap-2 p-4 bg-gray-50 rounded-2xl group cursor-pointer hover:bg-sky-50 transition-all hover:scale-105">
                    <Waves size={20} className="text-sky-400 group-hover:rotate-12 transition-transform" />
                    <span className="text-[7px] font-black uppercase tracking-widest text-sky-900/40">Beach</span>
                  </div>
                  <div className="flex flex-col items-center gap-2 p-4 bg-gray-50 rounded-2xl group cursor-pointer hover:bg-emerald-50 transition-all hover:scale-105">
                    <Mountain size={20} className="text-emerald-400 group-hover:rotate-12 transition-transform" />
                    <span className="text-[7px] font-black uppercase tracking-widest text-emerald-900/40">Hills</span>
                  </div>
                  <div className="flex flex-col items-center gap-2 p-4 bg-gray-50 rounded-2xl group cursor-pointer hover:bg-amber-50 transition-all hover:scale-105">
                    <Palmtree size={20} className="text-amber-400 group-hover:rotate-12 transition-transform" />
                    <span className="text-[7px] font-black uppercase tracking-widest text-amber-900/40">Trpcal</span>
                  </div>
                </div>
              </div>
            </aside>

            {/* Right Column: Listing Stream */}
            <div className="flex-grow">
              <div className="flex flex-col md:flex-row items-end justify-between mb-16 border-b border-gray-50 pb-12 gap-8">
                <div>
                  <span className="text-sky-500 font-black uppercase text-[10px] tracking-[0.3em] mb-4 inline-block">Global Inventory</span>
                  <h2 className="text-4xl md:text-5xl font-black tracking-tighter text-sky-950 leading-none">Property <span className="text-sky-600 italic">Stream</span></h2>
                  <p className="text-[10px] font-black text-gray-300 uppercase tracking-[0.2em] mt-6">Indexing {filteredAccommodations.length} High-End Sanctuaries Across the Region</p>
                </div>
                <div className="flex gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-sky-950 flex items-center justify-center text-white shadow-2xl shadow-sky-950/20"><Building2 size={20} /></div>
                </div>
              </div>

              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-40 gap-6">
                  <Loader2 className="animate-spin text-sky-200" size={64} strokeWidth={1} />
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-300">Synchronizing Master Registry...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  {filteredAccommodations.length > 0 ? (
                    filteredAccommodations.map((acc, idx) => (
                      <div
                        key={acc._id}
                        className="group flex flex-col h-full animate-fade-in"
                        style={{ animationDelay: `${idx * 0.1}s` }}
                      >
                        {/* Visual Card */}
                        <div className="relative h-[28rem] rounded-[3.5rem] overflow-hidden shadow-sm group-hover:shadow-2xl group-hover:-translate-y-2 transition-all duration-700">
                          <img
                            src={acc.images?.[0] || "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?q=80&w=1000&auto=format&fit=crop"}
                            alt={acc.accommodationName}
                            className="w-full h-full object-cover transition-transform duration-[2s] group-hover:scale-110"
                            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?q=80&w=1000&auto=format&fit=crop"; }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-sky-950/95 via-sky-950/20 to-transparent" />

                          <div className="absolute top-10 left-10 flex flex-col gap-3">
                            <span className="px-4 py-1.5 bg-white/20 backdrop-blur-md rounded-full text-[9px] font-black text-white uppercase tracking-widest border border-white/20 w-fit">
                              {acc.type}
                            </span>
                          </div>

                          <div className="absolute top-10 right-10">
                            <button className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 flex items-center justify-center text-white hover:bg-sky-500 hover:border-sky-500 transition-all shadow-xl group/fav">
                              <Star size={20} className="group-hover/fav:fill-white transition-all" />
                            </button>
                          </div>

                          <div className="absolute bottom-10 left-10 right-10 text-white">
                            <div className="flex items-center gap-3 text-sky-300 mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 translate-y-4 group-hover:translate-y-0">
                              <Star size={10} fill="currentColor" />
                              <span className="text-[9px] font-black uppercase tracking-[0.4em]">Elite Verified</span>
                            </div>
                            <h3 className="text-3xl font-black tracking-tight leading-tight mb-8 group-hover:text-sky-300 transition-colors drop-shadow-2xl truncate">{acc.accommodationName}</h3>

                            <div className="flex items-center justify-between border-t border-white/10 pt-8 mt-2">
                              <div className="flex items-center gap-6">
                                <div className="flex flex-col">
                                  <span className="text-[9px] font-black text-white/40 uppercase tracking-widest mb-1">Region</span>
                                  <span className="text-xs font-black flex items-center gap-2"><MapPin size={12} className="text-rose-400" /> {acc.location}</span>
                                </div>
                              </div>
                              <div className="text-right">
                                <span className="text-[9px] font-black text-white/40 uppercase tracking-widest mb-1 block">Nightly Rate</span>
                                <span className="text-2xl font-black leading-none">{acc.pricePerNight.toLocaleString()} <span className="text-[10px] text-sky-400">LKR</span></span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Narrative Snippet */}
                        <div className="pt-10 px-6 flex flex-col flex-grow">
                          <p className="text-gray-400 font-serif text-lg leading-relaxed line-clamp-2 h-14 mb-10 italic opacity-80 border-l-4 border-gray-50 pl-6">
                            "{acc.description}"
                          </p>
                          <button
                            onClick={() => handleBookNow(acc)}
                            disabled={!acc.availability}
                            className={`
                                w-full py-5 rounded-[2rem] text-[10px] font-black uppercase tracking-[0.3em] flex items-center justify-center gap-4 transition-all shadow-xl active:scale-95 group/btn
                                ${acc.availability
                                ? "bg-sky-950 text-white hover:bg-sky-800 shadow-sky-950/20"
                                : "bg-gray-100 text-gray-400 cursor-not-allowed"}
                            `}
                          >
                            {acc.availability ? (
                              <>
                                Secure Reservation
                                <ArrowRight size={18} className="group-hover/btn:translate-x-3 transition-transform duration-500" />
                              </>
                            ) : "Reserved Registry"}
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full py-40 flex flex-col items-center justify-center bg-gray-50/50 rounded-[4rem] border-2 border-dashed border-gray-100 gap-8">
                      <div className="w-28 h-28 bg-white rounded-full shadow-inner flex items-center justify-center text-gray-200">
                        <Hotel size={56} strokeWidth={1} />
                      </div>
                      <div className="text-center max-w-sm">
                        <h3 className="text-2xl font-black text-sky-950 italic tracking-tight">Vibe Not Detected</h3>
                        <p className="text-[11px] text-gray-400 font-bold mt-4 leading-relaxed uppercase tracking-widest">Our scouts haven't documented a sanctuary matching these parameters yet.</p>
                        <button
                          onClick={() => { setSearch(""); setFilterType("All"); setFilterLocation("All"); setPriceRange([0, 500000]); }}
                          className="mt-12 px-10 py-4 bg-sky-950 text-white rounded-2xl text-[9px] font-black uppercase tracking-[0.3em] shadow-2xl shadow-sky-950/20 active:scale-95"
                        >
                          Reset Registry Hub
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Luxury Trust Section */}
      <section className="bg-sky-950 py-24 mb-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-sky-900 rounded-full -mr-48 -mt-48 opacity-20" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-sky-800 rounded-full -ml-48 -mb-48 opacity-10" />

        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
          <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight mb-8">TravelAura <span className="italic text-sky-400">Elite Stays</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mt-16">
            <div className="space-y-4">
              <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center text-sky-400 mx-auto border border-white/10 group hover:bg-sky-500 hover:text-white transition-all">
                <ShieldCheck size={32} />
              </div>
              <h4 className="text-white font-black uppercase text-xs tracking-widest">Verified Luxury</h4>
              <p className="text-white/40 text-xs font-bold leading-relaxed px-6">Every property undergoes a rigorous 50-point inspection to ensure world-class standards.</p>
            </div>
            <div className="space-y-4">
              <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center text-amber-400 mx-auto border border-white/10 group hover:bg-amber-500 hover:text-white transition-all">
                <Coins size={32} />
              </div>
              <h4 className="text-white font-black uppercase text-xs tracking-widest">Price Guarantee</h4>
              <p className="text-white/40 text-xs font-bold leading-relaxed px-6">Direct partnerships with owners allow us to guarantee the most competitive rates available.</p>
            </div>
            <div className="space-y-4">
              <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center text-emerald-400 mx-auto border border-white/10 group hover:bg-emerald-500 hover:text-white transition-all">
                <CheckCircle2 size={32} />
              </div>
              <h4 className="text-white font-black uppercase text-xs tracking-widest">Seamless Flow</h4>
              <p className="text-white/40 text-xs font-bold leading-relaxed px-6">Instant confirmation and zero hidden fees. Experience travel as it should be—effortless.</p>
            </div>
          </div>
        </div>
      </section>

      <Fotter />

      {/* Integrated Styling */}
      <style>{`
        @keyframes slow-zoom {
          0% { transform: scale(1); }
          100% { transform: scale(1.1); }
        }
        .animate-slow-zoom { animation: slow-zoom 20s ease-in-out infinite alternate; }
        .animate-fade-in { animation: fadeIn 0.8s ease-out forwards; }
        .animate-slide-up { animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-slide-up-delayed { animation: slideUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards 0.2s; opacity: 0; }
        .animate-slide-up-more { animation: slideUp 1s cubic-bezier(0.16, 1, 0.3, 1) forwards 0.4s; opacity: 0; }

        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { 
          from { opacity: 0; transform: translateY(30px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
      `}</style>
    </div>
  );
}

export default UserAccommodation;